// Work.tsx — Client Stories / Portfolio page (/work)
// Layout: H1 + subtitle → filter tabs → featured case study → masonry card grid
//         with coming soon placeholders → logo marquee → testimonials → CTA
// Design: Indexed dark system, Barlow font, #57d7e2 cyan accent
// Mobile-first: all sections use 20px horizontal padding on mobile, 72px on desktop

import React, { useState, useMemo } from "react";
import { Link } from "wouter";
import SiteLayout from "@/components/SiteLayout";
import { CASE_STUDIES } from "@/lib/caseStudyData";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import MobileSlider from "@/components/MobileSlider";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";
const CDN = "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj";

const LOGO_ASSETS = [
  `/4941-9030.webp`, `/4941-9031.webp`,
  `/4941-9032.webp`, `/4941-9033.webp`,
  `/4941-9034.webp`, `/4941-9035.webp`,
  `/4941-9036.webp`, `/4941-9037.webp`,
  `/4941-9038.webp`, `/4941-9039.webp`,
  `/4941-9040.webp`, `/4941-9041.webp`,
  `/4941-9042.webp`, `/4941-9043.webp`,
  `/4941-9044.webp`, `/4941-9045.webp`,
  `/4941-9046.webp`, `/4941-9047.webp`,
  `/4941-9048.webp`, `/4941-9049.webp`,
  `/4941-9050.webp`, `/4941-9051.webp`,
  `/4941-9052.webp`, `/4941-9053.webp`,
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj/smartfog_87dd78bd.webp",
];

const TESTIMONIALS = [
  { quote: "Exceptional communication throughout the project. The team showed a deep grasp of our goals and delivered a flawless final product that exceeded our expectations.", name: "Flavio", role: "Growth Marketing Director", company: "B2B SaaS" },
  { quote: "Great team to work with, very happy with the consultancy and final documents. They translated complex SEO strategy into something our whole team could act on.", name: "Kirsten", role: "Senior Marketing Manager", company: "Professional Services" },
  { quote: "Indexed turned SEO from a cost center into our highest-ROI acquisition channel. The pipeline numbers speak for themselves — $1.5m in 10 months.", name: "James", role: "CEO", company: "Renewable Energy" },
  { quote: "The level of technical depth was unlike anything I'd seen from an SEO agency. They found issues our in-house team had missed for years and fixed them within weeks.", name: "Sarah", role: "Head of Digital", company: "FinTech" },
  { quote: "We went from page 3 to position 1 for our core terms in under 5 months. The revenue attribution reporting made it easy to justify the investment to our board.", name: "Marcus", role: "VP Marketing", company: "Enterprise SaaS" },
  { quote: "Indexed didn't just improve our rankings — they rebuilt how we think about search. The GEO work alone has opened up a channel we didn't even know existed.", name: "Priya", role: "CMO", company: "Biotech" },
];

// Build filter tabs from case study data
function getIndustryFilters(): string[] {
  const industries = new Set<string>();
  CASE_STUDIES.forEach(cs => {
    // Extract the industry part before the dot/separator
    const industry = cs.industryLabel.split("·")[0].trim();
    industries.add(industry);
  });
  return ["All", ...Array.from(industries)];
}

// ── Featured case study card ──────────────────────────────────────────────────
function FeaturedCard({ cs }: { cs: typeof CASE_STUDIES[0] }) {
  const isMobile = useIsMobile();
  const [hov, setHov] = useState(false);
  return (
    <Link asChild href={`/client-stories/${cs.slug}`}>
      <a
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          background: "#111",
          border: `1px solid ${hov ? "rgba(87,215,226,0.3)" : "rgba(255,255,255,0.08)"}`,
          borderRadius: "20px",
          overflow: "hidden",
          textDecoration: "none",
          transition: "border-color 0.2s, transform 0.2s",
          transform: hov ? "translateY(-2px)" : "translateY(0)",
          cursor: "pointer",
          minHeight: isMobile ? "auto" : "380px",
        }}
      >
        {isMobile && (
          <div style={{ height: "220px", overflow: "hidden", position: "relative" }}>
            {/* The featured photo is the page's largest element: load it at once. */}
            <img loading="eager" fetchPriority="high" src={cs.image} alt={cs.headline} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </div>
        )}

        <div style={{ padding: isMobile ? "28px 24px" : "48px 48px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
              <span style={{
                display: "inline-block", padding: "4px 12px", borderRadius: "4px",
                fontSize: "11px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.08em",
                textTransform: "uppercase" as const, color: cyan,
                background: "rgba(87,215,226,0.1)", border: "1px solid rgba(87,215,226,0.2)",
              }}>{cs.industryLabel}</span>
              <span style={{ color: "rgba(255,255,255,0.25)", fontSize: "13px", fontFamily: ff }}>{cs.timeframe}</span>
            </div>
            <h3 style={{
              color: "#fff", fontSize: isMobile ? "22px" : "clamp(22px, 2.4vw, 30px)",
              fontWeight: 800, fontFamily: ff, margin: "0 0 16px", lineHeight: 1.2, letterSpacing: "-0.02em",
            }}>{cs.cardHeadline}</h3>
            <p style={{
              color: "rgba(255,255,255,0.5)", fontSize: "16px", fontFamily: ff,
              lineHeight: 1.7, margin: "0 0 28px",
            }}>{cs.summary}</p>
          </div>

          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "24px" }}>
              {cs.metrics.slice(0, 3).map((m, i) => (
                <div key={i}>
                  <div style={{ color: cyan, fontSize: isMobile ? "20px" : "24px", fontWeight: 800, fontFamily: ff, lineHeight: 1 }}>{m.value}</div>
                  <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "12px", fontFamily: ff, marginTop: "4px", lineHeight: 1.3 }}>{m.label}</div>
                </div>
              ))}
            </div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              color: hov ? cyan : "rgba(255,255,255,0.5)",
              fontSize: "15px", fontWeight: 700, fontFamily: ff, transition: "color 0.15s",
            }}>
              Read case study
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ transition: "transform 0.15s", transform: hov ? "translateX(3px)" : "translateX(0)" }}>
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        {!isMobile && (
          <div style={{ position: "relative", overflow: "hidden" }}>
            <img loading="eager" fetchPriority="high" src={cs.image} alt={cs.headline} style={{
              width: "100%", height: "100%", objectFit: "cover", display: "block",
              transition: "transform 0.5s ease", transform: hov ? "scale(1.04)" : "scale(1)",
            }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(17,17,17,0.5) 0%, transparent 40%)" }} />
          </div>
        )}
      </a>
    </Link>
  );
}

// ── Standard case study card (supports wide variant) ─────────────────────────
function CaseStudyCard({ cs, wide = false }: { cs: typeof CASE_STUDIES[0]; wide?: boolean }) {
  const [hov, setHov] = useState(false);
  const isMobile = useIsMobile();

  if (wide && !isMobile) {
    // Wide variant: horizontal layout like a mini featured card
    return (
      <Link asChild href={`/client-stories/${cs.slug}`}>
        <a
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            background: "#111",
            border: `1px solid ${hov ? "rgba(87,215,226,0.25)" : "rgba(255,255,255,0.07)"}`,
            borderRadius: "16px", overflow: "hidden", textDecoration: "none",
            transition: "border-color 0.2s, transform 0.2s",
            transform: hov ? "translateY(-3px)" : "translateY(0)",
            cursor: "pointer", height: "100%",
          }}
        >
          <div style={{ padding: "28px 28px 28px", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
              <span style={{
                display: "inline-block", padding: "3px 9px", borderRadius: "3px",
                fontSize: "11px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.07em",
                textTransform: "uppercase" as const, color: cyan,
                background: "rgba(87,215,226,0.08)", border: "1px solid rgba(87,215,226,0.15)",
              }}>{cs.industryLabel}</span>
              <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "12px", fontFamily: ff }}>{cs.timeframe}</span>
            </div>
            <h3 style={{
              color: "#fff", fontSize: "20px", fontWeight: 800, fontFamily: ff,
              margin: "0 0 10px", lineHeight: 1.3, letterSpacing: "-0.01em",
            }}>{cs.cardHeadline}</h3>
            <p style={{
              color: "rgba(255,255,255,0.45)", fontSize: "15px", fontFamily: ff,
              lineHeight: 1.65, margin: "0 0 20px", flex: 1,
            }}>{cs.summary}</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginBottom: "16px" }}>
              {cs.metrics.slice(0, 3).map((m, i) => (
                <div key={i} style={{
                  padding: "10px 12px", background: "rgba(87,215,226,0.05)",
                  border: "1px solid rgba(87,215,226,0.1)", borderRadius: "8px",
                }}>
                  <div style={{ color: cyan, fontSize: "16px", fontWeight: 800, fontFamily: ff, lineHeight: 1 }}>{m.value}</div>
                  <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "11px", fontFamily: ff, marginTop: "3px", lineHeight: 1.3 }}>{m.label}</div>
                </div>
              ))}
            </div>
            <div style={{
              display: "flex", alignItems: "center", gap: "5px",
              color: hov ? cyan : "rgba(255,255,255,0.35)",
              fontSize: "13px", fontWeight: 700, fontFamily: ff, transition: "color 0.15s",
            }}>
              Read case study
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ transition: "transform 0.15s", transform: hov ? "translateX(3px)" : "translateX(0)" }}>
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div style={{ overflow: "hidden", position: "relative" }}>
            <img loading="lazy" src={cs.image} alt={cs.headline} style={{
              width: "100%", height: "100%", objectFit: "cover", display: "block",
              transition: "transform 0.4s ease", transform: hov ? "scale(1.05)" : "scale(1)",
            }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(17,17,17,0.4) 0%, transparent 30%)" }} />
          </div>
        </a>
      </Link>
    );
  }

  // Standard vertical card
  return (
    <Link asChild href={`/client-stories/${cs.slug}`}>
      <a
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: "flex", flexDirection: "column",
          background: "#111",
          border: `1px solid ${hov ? "rgba(87,215,226,0.25)" : "rgba(255,255,255,0.07)"}`,
          borderRadius: "16px", overflow: "hidden", textDecoration: "none",
          transition: "border-color 0.2s, transform 0.2s",
          transform: hov ? "translateY(-3px)" : "translateY(0)",
          cursor: "pointer", height: "100%",
        }}
      >
        <div style={{ height: "200px", overflow: "hidden", position: "relative", flexShrink: 0 }}>
          <img loading="lazy" src={cs.image} alt={cs.headline} style={{
            width: "100%", height: "100%", objectFit: "cover", display: "block",
            transition: "transform 0.4s ease", transform: hov ? "scale(1.05)" : "scale(1)",
          }} />
        </div>
        <div style={{ padding: "24px 24px 28px", display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
            <span style={{
              display: "inline-block", padding: "3px 9px", borderRadius: "3px",
              fontSize: "11px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.07em",
              textTransform: "uppercase" as const, color: cyan,
              background: "rgba(87,215,226,0.08)", border: "1px solid rgba(87,215,226,0.15)",
            }}>{cs.industryLabel}</span>
            <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "12px", fontFamily: ff }}>{cs.timeframe}</span>
          </div>
          <h3 style={{
            color: "#fff", fontSize: "18px", fontWeight: 800, fontFamily: ff,
            margin: "0 0 10px", lineHeight: 1.3, letterSpacing: "-0.01em",
          }}>{cs.cardHeadline}</h3>
          <p style={{
            color: "rgba(255,255,255,0.45)", fontSize: "15px", fontFamily: ff,
            lineHeight: 1.65, margin: "0 0 20px", flex: 1,
            display: "-webkit-box", WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical" as const, overflow: "hidden",
          }}>{cs.summary}</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "18px" }}>
            {cs.metrics.slice(0, 2).map((m, i) => (
              <div key={i} style={{
                padding: "10px 12px", background: "rgba(87,215,226,0.05)",
                border: "1px solid rgba(87,215,226,0.1)", borderRadius: "8px",
              }}>
                <div style={{ color: cyan, fontSize: "18px", fontWeight: 800, fontFamily: ff, lineHeight: 1 }}>{m.value}</div>
                <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "12px", fontFamily: ff, marginTop: "4px", lineHeight: 1.3 }}>{m.label}</div>
              </div>
            ))}
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: "5px",
            color: hov ? cyan : "rgba(255,255,255,0.35)",
            fontSize: "13px", fontWeight: 700, fontFamily: ff, transition: "color 0.15s",
          }}>
            Read case study
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ transition: "transform 0.15s", transform: hov ? "translateX(3px)" : "translateX(0)" }}>
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </a>
    </Link>
  );
}

// ── Filter Tabs ──────────────────────────────────────────────────────────────
function FilterTabs({ active, tabs, onChange, isMobile }: {
  active: string; tabs: string[]; onChange: (t: string) => void; isMobile: boolean;
}) {
  return (
    <div style={{
      display: "flex", gap: "8px", flexWrap: "nowrap",
      overflowX: "auto", WebkitOverflowScrolling: "touch",
      paddingBottom: "4px",
      msOverflowStyle: "none", scrollbarWidth: "none",
    }}>
      {tabs.map(tab => {
        const isActive = tab === active;
        return (
          <button
            key={tab}
            onClick={() => onChange(tab)}
            style={{
              flexShrink: 0,
              padding: "8px 18px",
              borderRadius: "20px",
              border: `1px solid ${isActive ? "rgba(87,215,226,0.4)" : "rgba(255,255,255,0.08)"}`,
              background: isActive ? "rgba(87,215,226,0.12)" : "rgba(255,255,255,0.03)",
              color: isActive ? cyan : "rgba(255,255,255,0.45)",
              fontSize: "13px", fontWeight: 600, fontFamily: ff,
              cursor: "pointer",
              transition: "all 0.2s",
              outline: "none",
            }}
          >
            {tab}
          </button>
        );
      })}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────
export default function Work() {
  useSeoMeta({
    title: "Our Work | SEO Case Studies & Proven Client Results",
    description: "See how we've helped businesses grow organic traffic, improve search rankings, and drive measurable revenue through strategic, long-term SEO investment.",
    canonical: "/client-stories",
    ogType: "website",
    jsonLd: [{
      "@type": "ItemList",
      "name": "Indexed Client Case Studies",
      "url": "https://www.joinindexed.com/client-stories",
      "description": "Real results from real clients: revenue attribution, traffic growth, and AI search visibility."
    }],
  });

  const isMobile = useIsMobile();
  const filterTabs = useMemo(() => getIndustryFilters(), []);
  const [activeFilter, setActiveFilter] = useState("All");

  const filteredStudies = useMemo(() => {
    if (activeFilter === "All") return CASE_STUDIES;
    return CASE_STUDIES.filter(cs => cs.industryLabel.split("·")[0].trim() === activeFilter);
  }, [activeFilter]);

  const featured = filteredStudies[0];
  const rest = filteredStudies.slice(1);

  return (
    <SiteLayout>
      <div style={{ background: "#0a0a0a", minHeight: "100vh", paddingTop: "80px" }}>

        {/* ── HERO ─────────────────────────────────────────────────────────── */}
        {/* Responsive layout is CSS-driven (media query at the 900px breakpoint),
            NOT JS isMobile branching. The hero is prerendered to static HTML at a
            mobile viewport; if its responsive values came from isMobile inline
            styles they'd bake as mobile and visibly reflow when React hydrates at
            desktop width. Keeping them in this <style> block means the prerendered
            HTML carries the media query and paints the correct layout immediately,
            so nothing shifts on load. */}
        <style>{`
          .work-hero-section { padding: 96px 72px 48px; }
          .work-hero-h1 { font-size: clamp(40px, 4vw, 60px); }
          .work-hero-desc { font-size: 18px; }
          @media (max-width: 900px) {
            .work-hero-section { padding: 60px 20px 40px; }
            .work-hero-h1 { font-size: 36px; }
            .work-hero-desc { font-size: 16px; }
          }
        `}</style>
        <section className="work-hero-section" style={{ maxWidth: "1440px", margin: "0 auto", boxSizing: "border-box" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "20px" }}>
            <h1 className="work-hero-h1" style={{
              color: "white", fontWeight: 700,
              lineHeight: 1.08, margin: 0, fontFamily: ff, letterSpacing: "-0.5px",
            }}>
              Case studies that{" "}<span style={{ color: cyan }}>speak for themselves.</span>
            </h1>
            <p className="work-hero-desc" style={{
              color: "rgba(255,255,255,0.5)", fontFamily: ff,
              lineHeight: 1.7, margin: 0, maxWidth: "560px",
            }}>
              Real clients. Real results. A closer look at the brands we've helped scale through search — and the revenue that followed.
            </p>
          </div>
        </section>

        {/* ── FILTER TABS ─────────────────────────────────────────────────── */}
        <section style={{ padding: isMobile ? "0 20px 24px" : "0 72px 32px", maxWidth: "1440px", margin: "0 auto", boxSizing: "border-box" }}>
          <FilterTabs active={activeFilter} tabs={filterTabs} onChange={setActiveFilter} isMobile={isMobile} />
        </section>

        {/* ── FEATURED CASE STUDY ──────────────────────────────────────────── */}
        {featured && (
          <section style={{
            padding: isMobile ? "0 20px 24px" : "0 72px 32px",
            maxWidth: "1440px", margin: "0 auto", boxSizing: "border-box",
            transition: "opacity 0.3s", opacity: 1,
          }}>
            <FeaturedCard cs={featured} />
          </section>
        )}

        {/* ── MASONRY CARD GRID ───────────────────────────────────────────── */}
        <section style={{
          padding: isMobile ? "0 20px 60px" : "0 72px 96px",
          maxWidth: "1440px", margin: "0 auto", boxSizing: "border-box",
        }}>
          {isMobile ? (
            <MobileSlider itemWidth={300} gap={16} padding={20}>
              {rest.map((cs) => (
                <CaseStudyCard key={cs.slug} cs={cs} />
              ))}
            </MobileSlider>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* Row 1: 2 wide cards */}
              {rest.length >= 2 && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  {rest.slice(0, 2).map((cs) => (
                    <CaseStudyCard key={cs.slug} cs={cs} wide />
                  ))}
                </div>
              )}
              {/* Row 2: 3 standard cards */}
              {rest.length >= 3 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
                  {rest.slice(2, 5).map((cs) => (
                    <CaseStudyCard key={cs.slug} cs={cs} />
                  ))}
                </div>
              )}
              {/* Row 3: remaining cards */}
              {rest.slice(5).length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
                  {rest.slice(5).map((cs) => (
                    <CaseStudyCard key={cs.slug} cs={cs} />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── LOGO MARQUEE ─────────────────────────────────────────────────── */}
        <section style={{ background: "#080a0a", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{
            maxWidth: "860px", margin: "0 auto",
            padding: isMobile ? "24px 20px" : "32px 72px",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: isMobile ? "16px" : "0",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}>
            {[
              { stat: "3.5x", label: "Average ROI on SEO investment" },
              { stat: "5.0 ★", label: "From 50+ verified client reviews" },
            ].map((item, i) => (
              <div key={i} style={{
                padding: isMobile ? "12px 0" : "8px 32px",
                borderRight: (!isMobile && i < 1) ? "1px solid rgba(255,255,255,0.08)" : "none",
                borderBottom: (isMobile && i < 1) ? "1px solid rgba(255,255,255,0.08)" : "none",
                paddingBottom: (isMobile && i < 1) ? "16px" : undefined,
                display: "flex", flexDirection: "column", gap: "4px",
                alignItems: isMobile ? "flex-start" : "center",
                textAlign: isMobile ? "left" : "center" as const,
              }}>
                <div style={{ color: cyan, fontSize: "28px", fontWeight: 700, fontFamily: ff, lineHeight: 1, letterSpacing: "-0.02em" }}>{item.stat}</div>
                <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", fontFamily: ff, lineHeight: 1.4 }}>{item.label}</div>
              </div>
            ))}
          </div>
          <div style={{ position: "relative", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "16px 0", overflow: "hidden" }}>
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, #080a0a 0%, transparent 12%, transparent 88%, #080a0a 100%)", zIndex: 2, pointerEvents: "none" }} />
            <div style={{ display: "flex", alignItems: "center", gap: "50px", animation: "scroll-logos 50s linear infinite", width: "max-content" }}>
              {[...LOGO_ASSETS, ...LOGO_ASSETS].map((src, idx) => (
                <img loading="lazy" key={idx} src={src} alt={`client logo ${idx}`} style={{ width: "135px", height: "90px", objectFit: "contain", flexShrink: 0, filter: "grayscale(1) brightness(2)" }} />
              ))}
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
        <section style={{ background: "#0a0c0c", padding: isMobile ? "60px 20px" : "100px 72px" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <h2 style={{ color: "white", fontSize: isMobile ? "30px" : "48px", fontWeight: 700, fontFamily: ff, margin: "0 0 40px", lineHeight: 1.1 }}>
              What clients say<br /><span style={{ color: cyan }}>about working with us.</span>
            </h2>
            {(() => {
              const renderTestimonial = (t: typeof TESTIMONIALS[0], i: number) => (
                <div key={i} style={{ background: "#111111", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: isMobile ? "24px" : "36px", display: "flex", flexDirection: "column", gap: "20px", height: "100%", boxSizing: "border-box" }}>
                  <div style={{ display: "flex", gap: "3px" }}>
                    {[...Array(5)].map((_, s) => (
                      <svg key={s} width="14" height="14" viewBox="0 0 12 12" fill={cyan}><path d="M6 1l1.5 3 3.5.5-2.5 2.5.5 3.5L6 9l-3 1.5.5-3.5L1 4.5l3.5-.5z"/></svg>
                    ))}
                  </div>
                  <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "16px", fontFamily: ff, lineHeight: 1.75, margin: 0, flex: 1, fontStyle: "italic" }}>
                    "{t.quote}"
                  </p>
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "16px", display: "flex", alignItems: "center", gap: "14px" }}>
                    <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "linear-gradient(135deg, rgba(87,215,226,0.3), rgba(87,215,226,0.1))", border: "1px solid rgba(87,215,226,0.4)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ color: cyan, fontSize: "15px", fontWeight: 700, fontFamily: ff }}>{t.name[0]}</span>
                    </div>
                    <div>
                      <div style={{ color: "white", fontSize: "14px", fontWeight: 700, fontFamily: ff }}>{t.name}</div>
                      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", fontFamily: ff }}>{t.role} · {t.company}</div>
                    </div>
                  </div>
                </div>
              );
              return isMobile ? (
                <MobileSlider itemWidth={300} gap={16} padding={20}>
                  {TESTIMONIALS.map((t, i) => renderTestimonial(t, i))}
                </MobileSlider>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "18px" }}>
                  {TESTIMONIALS.map((t, i) => renderTestimonial(t, i))}
                </div>
              );
            })()}
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────────── */}
        <section style={{ background: "#080a0a", padding: isMobile ? "60px 20px" : "100px 72px", position: "relative", overflow: "hidden" }}>
          <div style={{ maxWidth: "760px", margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "28px" }}>
            <h2 style={{ color: "white", fontSize: isMobile ? "30px" : "48px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
              Find out what your site is losing.<br />{" "}<span style={{ color: cyan }}>It's free.</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: isMobile ? "16px" : "18px", fontFamily: ff, lineHeight: 1.7, margin: 0, maxWidth: "520px" }}>
              In a free personalized video review, a top-1% SEO records a walkthrough of your site and shows you exactly what's holding your rankings back — and what fixing it is worth. No pitch. No obligation.
            </p>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px", width: "100%" }}>
              <a
                href={STRATEGIST_URL}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  padding: "18px 40px", width: isMobile ? "100%" : "auto",
                  background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                  borderRadius: "88px", border: "1px solid #57d7e2",
                  boxShadow: "0px 4px 32px rgba(87,215,226,0.30)",
                  textDecoration: "none", color: "#0a0c0c",
                  fontSize: "17px", fontWeight: 700, fontFamily: ff, letterSpacing: "-0.01em",
                  transition: "opacity 0.2s, transform 0.2s", boxSizing: "border-box",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
              >
                Get my free video review →
              </a>
              <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "13px", fontFamily: ff }}>
                No call &middot; No pitch &middot; We take on 3-4 new clients per month
              </span>
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: isMobile ? "16px" : "0",
              marginTop: "16px", borderTop: "1px solid rgba(255,255,255,0.07)",
              paddingTop: "36px", width: "100%",
            }}>
              {[
                { metric: "5.0 ★", label: "from 100+ reviews" },
                { metric: "100+", label: "engagements delivered" },
                { metric: "Avg. 3.5x", label: "return on SEO investment" },
              ].map((s, i) => (
                <div key={s.metric} style={{
                  display: "flex", flexDirection: "column",
                  alignItems: isMobile ? "flex-start" : "center", gap: "4px",
                  paddingBottom: (isMobile && i < 2) ? "16px" : undefined,
                  borderBottom: (isMobile && i < 2) ? "1px solid rgba(255,255,255,0.07)" : "none",
                  borderRight: (!isMobile && i < 2) ? "1px solid rgba(255,255,255,0.1)" : "none",
                }}>
                  <span style={{ color: cyan, fontSize: "22px", fontWeight: 700, fontFamily: ff }}>{s.metric}</span>
                  <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </SiteLayout>
  );
}
